// ---------------------------------------------------------------------------
// Scroll Animation Support
// ---------------------------------------------------------------------------
// Produces data attributes for per-element scroll animations and provides
// the minimal IntersectionObserver script injected into the page.
//
// Animations are GPU-accelerated (opacity + transform only) and fire once
// per element (unobserve after trigger).

import { attr } from "./escape.js";

// ---------------------------------------------------------------------------
// Valid animation types — validated against a strict allowlist
// ---------------------------------------------------------------------------

const VALID_ANIMATION_TYPES = new Set([
  "fade-in",
  "slide-up",
  "slide-down",
  "slide-left",
  "slide-right",
  "scale-up",
  "zoom-in",
  "pop-in",
  "rotate-in",
  "rotate-in-left",
  "rotate-in-right",
  "flip-x",
  "flip-y",
  "bounce-up",
  "skew-in",
]);

function appendDataAttr(parts: string[], name: string, value: string | number | undefined): void {
  if (value === undefined) return;
  parts.push(`${name}="${attr(String(value))}"`);
}

function buildScrollEffectAttrs(scrollEffects: Record<string, unknown> | null): string[] {
  if (!scrollEffects) return [];

  const parts: string[] = [];

  const vertical = typeof scrollEffects.vertical === "object" && scrollEffects.vertical !== null
    ? scrollEffects.vertical as Record<string, unknown>
    : null;
  if (vertical?.enabled === true) {
    appendDataAttr(parts, "data-scroll-vertical-speed", typeof vertical.speed === "number" ? vertical.speed : 5);
    appendDataAttr(parts, "data-scroll-vertical-direction", typeof vertical.direction === "string" ? vertical.direction : "up");
  }

  const horizontal = typeof scrollEffects.horizontal === "object" && scrollEffects.horizontal !== null
    ? scrollEffects.horizontal as Record<string, unknown>
    : null;
  if (horizontal?.enabled === true) {
    appendDataAttr(parts, "data-scroll-horizontal-speed", typeof horizontal.speed === "number" ? horizontal.speed : 5);
  }

  const transparency = typeof scrollEffects.transparency === "object" && scrollEffects.transparency !== null
    ? scrollEffects.transparency as Record<string, unknown>
    : null;
  if (transparency?.enabled === true) {
    appendDataAttr(parts, "data-scroll-transparency-direction", typeof transparency.direction === "string" ? transparency.direction : "in");
    appendDataAttr(parts, "data-scroll-transparency-start", typeof transparency.viewportStart === "number" ? transparency.viewportStart : 0);
    appendDataAttr(parts, "data-scroll-transparency-end", typeof transparency.viewportEnd === "number" ? transparency.viewportEnd : 100);
  }

  const blur = typeof scrollEffects.blur === "object" && scrollEffects.blur !== null
    ? scrollEffects.blur as Record<string, unknown>
    : null;
  if (blur?.enabled === true) {
    appendDataAttr(parts, "data-scroll-blur-max", typeof blur.maxBlur === "number" ? blur.maxBlur : 10);
    appendDataAttr(parts, "data-scroll-blur-start", typeof blur.viewportStart === "number" ? blur.viewportStart : 0);
    appendDataAttr(parts, "data-scroll-blur-end", typeof blur.viewportEnd === "number" ? blur.viewportEnd : 100);
  }

  const rotate = typeof scrollEffects.rotate === "object" && scrollEffects.rotate !== null
    ? scrollEffects.rotate as Record<string, unknown>
    : null;
  if (rotate?.enabled === true) {
    appendDataAttr(parts, "data-scroll-rotate-direction", typeof rotate.direction === "string" ? rotate.direction : "cw");
    appendDataAttr(parts, "data-scroll-rotate-speed", typeof rotate.speed === "number" ? rotate.speed : 5);
    appendDataAttr(parts, "data-scroll-rotate-start", typeof rotate.viewportStart === "number" ? rotate.viewportStart : 0);
    appendDataAttr(parts, "data-scroll-rotate-end", typeof rotate.viewportEnd === "number" ? rotate.viewportEnd : 100);
  }

  const scale = typeof scrollEffects.scale === "object" && scrollEffects.scale !== null
    ? scrollEffects.scale as Record<string, unknown>
    : null;
  if (scale?.enabled === true) {
    appendDataAttr(parts, "data-scroll-scale-direction", typeof scale.direction === "string" ? scale.direction : "grow");
    appendDataAttr(parts, "data-scroll-scale-min", typeof scale.rangeMin === "number" ? scale.rangeMin : 0.5);
    appendDataAttr(parts, "data-scroll-scale-max", typeof scale.rangeMax === "number" ? scale.rangeMax : 1.5);
    appendDataAttr(parts, "data-scroll-scale-start", typeof scale.viewportStart === "number" ? scale.viewportStart : 0);
    appendDataAttr(parts, "data-scroll-scale-end", typeof scale.viewportEnd === "number" ? scale.viewportEnd : 100);
  }

  return parts;
}

// ---------------------------------------------------------------------------
// Data attribute builder
// ---------------------------------------------------------------------------

/**
 * Returns a string of data attributes for animation, or null if no animation.
 * Example: ` data-ease-anim="slide-up" data-ease-delay="200" data-ease-duration="400"`
 */
export function buildAnimationAttrs(
  props: Record<string, unknown>,
): string | null {
  const dssAnimation = typeof props._dssStyles === "object" && props._dssStyles !== null
    && typeof (props._dssStyles as Record<string, unknown>).animation === "object"
    && (props._dssStyles as Record<string, unknown>).animation !== null
      ? (props._dssStyles as Record<string, unknown>).animation as Record<string, unknown>
      : null;

  const animationType = typeof dssAnimation?.entranceType === "string"
    ? dssAnimation.entranceType
    : typeof dssAnimation?.entrance === "string"
      ? dssAnimation.entrance
      : props.animationType;
  const scrollEffects = typeof dssAnimation?.scrollEffects === "object" && dssAnimation.scrollEffects !== null
    ? dssAnimation.scrollEffects as Record<string, unknown>
    : null;
  const parts = buildScrollEffectAttrs(scrollEffects);

  if (
    typeof animationType === "string" &&
    animationType !== "none" &&
    VALID_ANIMATION_TYPES.has(animationType)
  ) {
    const delay = typeof dssAnimation?.delayMs === "number"
      ? String(dssAnimation.delayMs)
      : typeof props.animationDelay === "string"
        ? props.animationDelay
        : "0";

    const duration = typeof dssAnimation?.durationMs === "number"
      ? String(dssAnimation.durationMs)
      : typeof props.animationDuration === "string"
        ? props.animationDuration
        : "400";

    const threshold = typeof dssAnimation?.scrollThreshold === "number"
      ? String(dssAnimation.scrollThreshold)
      : "0.15";

    appendDataAttr(parts, "data-ease-anim", animationType);
    appendDataAttr(parts, "data-ease-delay", delay);
    appendDataAttr(parts, "data-ease-duration", duration);
    appendDataAttr(parts, "data-ease-easing", typeof dssAnimation?.easing === "string" ? dssAnimation.easing : "ease");
    appendDataAttr(parts, "data-ease-threshold", threshold);
  }

  return parts.length > 0 ? ` ${parts.join(" ")}` : null;
}

// ---------------------------------------------------------------------------
// Animation CSS — injected into the style block
// ---------------------------------------------------------------------------

export const ANIMATION_CSS = `[data-ease-anim]{opacity:0;transition-property:opacity,transform;transition-timing-function:cubic-bezier(0.16,1,0.3,1)}
[data-ease-anim="slide-up"]{transform:translateY(30px)}
[data-ease-anim="slide-down"]{transform:translateY(-30px)}
[data-ease-anim="slide-left"]{transform:translateX(-30px)}
[data-ease-anim="slide-right"]{transform:translateX(30px)}
[data-ease-anim="scale-up"]{transform:scale(0.9)}
[data-ease-anim="zoom-in"]{transform:scale(0.8)}
[data-ease-anim="pop-in"]{transform:translateY(12px) scale(0.96)}
[data-ease-anim="rotate-in"]{transform:rotate(-8deg) scale(0.96)}
[data-ease-anim="rotate-in-left"]{transform:translateX(-24px) rotate(-10deg)}
[data-ease-anim="rotate-in-right"]{transform:translateX(24px) rotate(10deg)}
[data-ease-anim="flip-x"]{transform:perspective(1000px) rotateX(-24deg)}
[data-ease-anim="flip-y"]{transform:perspective(1000px) rotateY(-24deg)}
[data-ease-anim="bounce-up"]{transform:translateY(40px) scale(0.94)}
[data-ease-anim="skew-in"]{transform:translateY(16px) skewY(-6deg)}
[data-ease-anim].ease-anim-visible{opacity:1;transform:none}
@media (prefers-reduced-motion: reduce){[data-ease-anim]{opacity:1!important;transform:none!important;transition:none!important}}`;

// ---------------------------------------------------------------------------
// IntersectionObserver script — injected before </body>
// ---------------------------------------------------------------------------
// Minimal, no dependencies. Fires once per element (unobserve after trigger).
// threshold 0.15 = trigger when 15% of element is visible.

export const ANIMATION_OBSERVER_SCRIPT = `(function(){var clamp=function(v,min,max){return Math.min(Math.max(v,min),max)};var prefersReducedMotion=typeof window.matchMedia==='function'&&window.matchMedia('(prefers-reduced-motion: reduce)').matches;var animatedEls=document.querySelectorAll('[data-ease-anim]');if(prefersReducedMotion){animatedEls.forEach(function(el){el.classList.add('ease-anim-visible');el.style.transition='none';el.style.opacity='1';el.style.transform='none'});return}animatedEls.forEach(function(el){el.style.transitionDuration=(el.dataset.easeDuration||'400')+'ms';el.style.transitionDelay=(el.dataset.easeDelay||'0')+'ms';el.style.transitionTimingFunction=el.dataset.easeEasing||'ease';var threshold=Number(el.dataset.easeThreshold||'0.15');if(!Number.isFinite(threshold)){threshold=0.15}threshold=Math.min(Math.max(threshold,0.05),0.9);new IntersectionObserver(function(entries,obs){entries.forEach(function(entry){if(entry.isIntersecting){entry.target.classList.add('ease-anim-visible');obs.unobserve(entry.target)}})},{threshold:threshold}).observe(el)});var scrollEls=Array.prototype.slice.call(document.querySelectorAll('[data-scroll-vertical-speed],[data-scroll-horizontal-speed],[data-scroll-transparency-direction],[data-scroll-blur-max],[data-scroll-rotate-direction],[data-scroll-scale-direction]'));if(!scrollEls.length)return;var ticking=false;var calcProgress=function(rect){var vh=window.innerHeight||1;return clamp(((vh-rect.top)/(vh+rect.height))*100,0,100)};var normalize=function(progress,start,end){var span=Math.max(end-start,1);return clamp((progress-start)/span,0,1)};var update=function(){ticking=false;scrollEls.forEach(function(el){var rect=el.getBoundingClientRect();var progress=calcProgress(rect);var transforms=[];var opacity='';var blur='';if(el.dataset.scrollVerticalSpeed){var verticalDir=el.dataset.scrollVerticalDirection==='down'?1:-1;var verticalOffset=(progress-50)*0.12*Number(el.dataset.scrollVerticalSpeed||'0')*verticalDir;transforms.push('translateY('+verticalOffset.toFixed(2)+'px)')}if(el.dataset.scrollHorizontalSpeed){var horizontalOffset=(progress-50)*0.12*Number(el.dataset.scrollHorizontalSpeed||'0');transforms.push('translateX('+horizontalOffset.toFixed(2)+'px)')}if(el.dataset.scrollRotateDirection){var rotateProgress=normalize(progress,Number(el.dataset.scrollRotateStart||'0'),Number(el.dataset.scrollRotateEnd||'100'));var rotateDir=el.dataset.scrollRotateDirection==='ccw'?-1:1;var degrees=rotateProgress*18*Number(el.dataset.scrollRotateSpeed||'0')*rotateDir;transforms.push('rotate('+degrees.toFixed(2)+'deg)')}if(el.dataset.scrollScaleDirection){var scaleProgress=normalize(progress,Number(el.dataset.scrollScaleStart||'0'),Number(el.dataset.scrollScaleEnd||'100'));var rangeMin=Number(el.dataset.scrollScaleMin||'0.5');var rangeMax=Number(el.dataset.scrollScaleMax||'1.5');var scaleValue=el.dataset.scrollScaleDirection==='shrink'?rangeMax-(rangeMax-rangeMin)*scaleProgress:rangeMin+(rangeMax-rangeMin)*scaleProgress;transforms.push('scale('+scaleValue.toFixed(3)+')')}if(el.dataset.scrollTransparencyDirection){var transparencyProgress=normalize(progress,Number(el.dataset.scrollTransparencyStart||'0'),Number(el.dataset.scrollTransparencyEnd||'100'));opacity=String(el.dataset.scrollTransparencyDirection==='out'?1-transparencyProgress:transparencyProgress)}if(el.dataset.scrollBlurMax){var blurProgress=normalize(progress,Number(el.dataset.scrollBlurStart||'0'),Number(el.dataset.scrollBlurEnd||'100'));blur='blur('+((1-blurProgress)*Number(el.dataset.scrollBlurMax||'0')).toFixed(2)+'px)'}el.style.transform=transforms.length?transforms.join(' '):'';el.style.opacity=opacity;el.style.filter=blur})};var onScroll=function(){if(!ticking){ticking=true;requestAnimationFrame(update)}};window.addEventListener('scroll',onScroll,{passive:true});window.addEventListener('resize',onScroll);update()})();`;
